import React, { useState, useEffect, useCallback } from 'react';
import { Copy } from 'lucide-react';
import { hasEditAccess } from '../permissionConfig';
import { notifyEditBlocked } from '../components/EditAccessToast';
import { S, EMBEDDED_CSS, DpoBeamBackground, ICON } from './distributorPoShared';
import { API_BASE } from '../config';

// Same clipboard helper GRNManagement.jsx uses for its copyable PO/GRN/ticket
// numbers — plain textarea fallback for browsers without navigator.clipboard.
function copyToClipboard(text, onDone) {
    if (!text) return;
    const value = String(text);
    if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(value).then(() => onDone && onDone()).catch(() => {});
    } else {
        const ta = document.createElement('textarea');
        ta.value = value;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy'); onDone && onDone(); } catch { /* ignore */ }
        document.body.removeChild(ta);
    }
}

// AccountsLayout sets data-acc-theme="dark" on a real DOM ancestor of this
// page (not portaled, unlike GRN's own toggle), so a plain descendant
// selector reaches it. Redefining the --dpo-* CSS variables scoped to just
// the right-block wrapper is enough to re-theme it — every card/table style
// below already reads color/background via var(--dpo-surface) etc., so nothing
// else needs to change. First pass per explicit request: transparent
// background, white border (same radius as the block already has), white text.
const INVOICE_QUEUE_DARK_CSS = `
[data-acc-theme="dark"] .acc-iq-rightcol,
[data-acc-theme="dark"] .acc-iq-leftcol {
  --dpo-text: #FFFFFF;
  --dpo-text-muted: #D9D9D9;
  --dpo-surface: transparent;
  --dpo-surface-subtle: rgba(255,255,255,0.06);
  --dpo-border: #FFFFFF;
  --dpo-border-soft: rgba(255,255,255,0.45);
  --dpo-purple: #FFFFFF;
  --dpo-purple-tint: rgba(255,255,255,0.14);
  --dpo-green: #FFFFFF;
  --dpo-green-tint: rgba(255,255,255,0.14);
  --dpo-amber: #FFFFFF;
  --dpo-amber-tint: rgba(255,255,255,0.14);
  --dpo-error: #FFFFFF;
  --dpo-pink-tint: rgba(255,255,255,0.14);
}
/* S.btnPrimary hardcodes a purple background (not a CSS var like everything
   else), so it needs a direct override to stay within the black/white/grey
   palette — inverted-primary look (white fill, black text) rather than the
   ghost buttons' transparent/white-border look. */
[data-acc-theme="dark"] .acc-iq-rightcol .acc-iq-btnPrimary {
  background: #FFFFFF !important;
  color: #000000 !important;
}
/* App.css has a global, unscoped tbody tr:nth-child(even)/tr:hover rule
   (light-grey stripe + mint hover) that leaks into every table lacking its
   own opt-out class — Accounts.css used to neutralize it via .acc-table;
   this table needs the same treatment, in both themes, not just dark. */
.acc-iq-table tbody tr:nth-child(even) { background: transparent; }
.acc-iq-table tbody tr:hover { background: var(--dpo-purple-tint); }
/* App.css also has an unscoped "tbody td { color: #334155 }" rule that beats
   plain inheritance (an element selector always outranks an inherited value)
   — this silently overrode every cell's text color in both themes. Class
   selector here outranks that plain element selector, so this wins instead. */
.acc-iq-table tbody td { color: var(--dpo-text); }
/* S.th reuses var(--dpo-text) AS a dark header background (works in light
   mode where --dpo-text is near-black) — dark mode redefines --dpo-text to
   white for readability elsewhere, which would flip the header to white-on-
   white. Pin the header to a fixed black bar instead, decoupled from that var. */
[data-acc-theme="dark"] .acc-iq-rightcol .acc-iq-table thead th {
  background: #000000 !important;
  color: #FFFFFF !important;
}
`;

// Reuses GRN/DistributorPO's 66/33 split-pane pattern (distributorPoShared.js
// S.splitBody/splitLeftCol/splitRightCol) but with the ratio flipped — the
// list here is compact (voucher/buyer/PO) while the detail needs room for a
// line-items table with batch/mfg/exp sub-lines, so list gets 33% and detail
// gets 66%, not the other way around like GRN's PO list + KPI panel.
const S2 = {
    leftCol: {
        flex: '0 0 33%', minWidth: 260, maxWidth: 340,
        position: 'sticky', top: 20, alignSelf: 'flex-start',
        maxHeight: 'calc(100vh - 160px)', overflowY: 'auto',
        display: 'flex', flexDirection: 'column', gap: 10,
    },
    rightCol: { flex: '1 1 66%', minWidth: 0 },
    soCard: {
        background: 'var(--dpo-surface)', border: '1px solid var(--dpo-border)', borderRadius: 12,
        padding: '12px 14px', cursor: 'pointer', boxShadow: '0 1px 2px rgba(35,28,48,0.05)',
    },
    soCardActive: { border: '1px solid var(--dpo-purple)', background: 'var(--dpo-purple-tint)' },
    batchLine: { fontSize: 13, color: 'var(--dpo-text-muted)', marginTop: 3, display: 'flex', alignItems: 'center', gap: 5 },
    copyIcon: { flex: 'none', display: 'inline-flex', alignItems: 'center', color: 'var(--dpo-text-muted)', cursor: 'pointer' },
    statStrip: { display: 'grid', gridTemplateColumns: '1.4fr 1.4fr 0.7fr 0.7fr 0.7fr', gap: 10 },
    statCard: { borderRadius: 10, padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 4, background: 'var(--dpo-surface-subtle)', border: '1px solid var(--dpo-border)', justifyContent: 'center', minHeight: 0 },
};

export default function AccountsInvoiceQueue({ user }) {
    const [soList,       setSoList]       = useState([]);
    const [search,       setSearch]       = useState('');
    const [statusFilter, setStatusFilter] = useState('normal'); // 'normal' | 'advance_booked'
    const [loading,      setLoading]      = useState(true);
    const [error,        setError]        = useState(null);
    const [selectedId,   setSelectedId]   = useState(null); // so.id or null
    const [invoiceNo,    setInvoiceNo]    = useState({});
    const [submitting,   setSubmitting]   = useState(null);
    const [editedQty,    setEditedQty]    = useState({}); // { lineItemId: value } — advance-booked SOs only
    const [savingQty,    setSavingQty]    = useState(false);
    const displayName = localStorage.getItem('display_name') || 'Accounts';

    const fetchSoList = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${API_BASE}/acc-so-list?view=invoice&search=${encodeURIComponent(search)}`);
            if (!res.ok) {
                throw new Error(`Server returned ${res.status}`);
            }
            const contentType = res.headers.get('content-type') || '';
            if (!contentType.includes('application/json')) {
                throw new Error('Got a non-JSON response — check API_BASE points to the backend, not the frontend host');
            }
            const data = await res.json();
            setSoList(data.so_list || []);
        } catch (err) {
            console.error('Failed to load invoice queue', err);
            setError(err.message);
            setSoList([]);
        } finally {
            setLoading(false);
        }
    }, [search]);

    useEffect(() => { fetchSoList(); }, [fetchSoList]);

    // Deep-link from a notification click (?q=<invoice_no>/<po_number>).
    useEffect(() => {
        const q = new URLSearchParams(window.location.search).get('q');
        if (q) setSearch(q);
    }, []);

    const openDetail = (so) => {
        setSelectedId(so.id);
        setEditedQty({});
        markVisited(so.voucher_no);
    };

    // Fire-and-forget — shared per-user "visited" tracking (record-visits.js)
    // that drives this section's sidebar badge count in AccountsLayout.jsx.
    const markVisited = (voucherNo) => {
        fetch(`${API_BASE}/record-visits?action=mark`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username: user?.username, section: 'invoice_queue', record_key: voucherNo }),
        }).catch(() => {});
    };

    const markInvoiced = async (so) => {
        setSubmitting(so.id);
        try {
            await fetch(`${API_BASE}/acc-invoice-complete`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    voucher_no:  so.voucher_no,
                    po_number:   so.order_ref_no,
                    invoice_no:  invoiceNo[so.id] || null,
                    invoiced_by: displayName,
                    line_items: (so.line_items || []).map(li => ({
                        ean:         li.ean,
                        sku_name:    li.sku_name,
                        so_qty:      li.so_qty,
                        invoice_qty: li.invoice_qty
                    }))
                })
            });
            setSelectedId(null);
            setEditedQty({});
            fetchSoList();
        } catch (err) {
            console.error('Failed to mark invoiced', err);
        } finally {
            setSubmitting(null);
        }
    };

    // Actual-qty editing for advance-booked SOs — same so_line_items.actual_qty
    // column SO Queue and the warehouse-scan flow both write to.
    const saveQuantities = async (so) => {
        const lineUpdates = (so.line_items || [])
            .filter(li => editedQty[li.id] !== undefined && editedQty[li.id] !== '')
            .map(li => ({ id: li.id, actual_qty: Number(editedQty[li.id]) }));

        if (lineUpdates.length === 0) return;

        setSavingQty(true);
        try {
            await fetch(`${API_BASE}/acc-advance-booking`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    voucher_no:   so.voucher_no,
                    booking_type: 'advance_booked',
                    updated_by:   displayName,
                    line_items:   lineUpdates
                })
            });
            setEditedQty({});
            const res  = await fetch(`${API_BASE}/acc-so-list?view=invoice&search=${encodeURIComponent(search)}`);
            const data = await res.json();
            setSoList(data.so_list || []);
        } catch (err) {
            console.error('Failed to save quantities', err);
        } finally {
            setSavingQty(false);
        }
    };

    const filteredList = soList.filter(so =>
        statusFilter === 'advance_booked' ? so.booking_type === 'advance_booked' : so.booking_type !== 'advance_booked'
    );
    const selectedSo = filteredList.find(so => so.id === selectedId) || null;
    const isAdvanceBooked = selectedSo?.booking_type === 'advance_booked';

    return (
        <div className="dpo-page" data-tutorial="invoice-queue-table" style={S.page}>
            <style>{EMBEDDED_CSS}{INVOICE_QUEUE_DARK_CSS}</style>

            <div style={S.headerBar}>
                <DpoBeamBackground />
                <h1 style={{ ...S.h1, position: 'relative', zIndex: 1 }}>Invoice Queue</h1>
                <input
                    style={{ ...S.input, position: 'relative', zIndex: 1, maxWidth: 280, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.14)', color: '#F3EFFA' }}
                    placeholder="Search voucher no, buyer, PO no..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
            </div>

            <div style={S.tabBar}>
                {[['normal', 'Normal'], ['advance_booked', 'Advance Booked']].map(([key, label]) => (
                    <span
                        key={key}
                        style={{ ...S.tab, ...(statusFilter === key ? S.tabActive : {}) }}
                        onClick={() => { setStatusFilter(key); setSelectedId(null); }}
                    >
                        {label}
                    </span>
                ))}
            </div>

            {loading ? (
                <p style={S.mutedNote}>Loading...</p>
            ) : error ? (
                <p style={{ ...S.mutedNote, color: 'var(--dpo-error)' }}>Failed to load: {error}</p>
            ) : (
                <div style={S.splitBody}>
                    <div className="acc-iq-leftcol" style={S2.leftCol}>
                        {filteredList.length === 0 ? (
                            <p style={S.mutedNote}>No SOs in this filter.</p>
                        ) : filteredList.map(so => (
                            <div
                                key={so.id}
                                style={{ ...S2.soCard, ...(so.id === selectedId ? S2.soCardActive : {}) }}
                                onClick={() => openDetail(so)}
                            >
                                <div style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--dpo-text)' }}>{so.voucher_no}</div>
                                <div style={{ fontSize: 12.5, color: 'var(--dpo-text-muted)', marginTop: 2 }}>{so.buyer || so.consignee || '—'}</div>
                                <div style={{ fontSize: 11.5, color: 'var(--dpo-text-muted)', marginTop: 2 }}>PO {so.order_ref_no || '—'}</div>
                            </div>
                        ))}
                    </div>

                    <div className="acc-iq-rightcol" style={S2.rightCol}>
                        {selectedSo ? (
                            <DetailPanel
                                so={selectedSo}
                                isAdvanceBooked={isAdvanceBooked}
                                editedQty={editedQty}
                                setEditedQty={setEditedQty}
                                invoiceNo={invoiceNo[selectedSo.id] || ''}
                                setInvoiceNo={v => setInvoiceNo(prev => ({ ...prev, [selectedSo.id]: v }))}
                                savingQty={savingQty}
                                submitting={submitting === selectedSo.id}
                                onSaveQuantities={() => {
                                    if (!hasEditAccess(user, 'invoicing', 'invoice_queue')) return notifyEditBlocked();
                                    saveQuantities(selectedSo);
                                }}
                                onMarkInvoiced={() => {
                                    if (!hasEditAccess(user, 'invoicing', 'invoice_queue')) return notifyEditBlocked();
                                    markInvoiced(selectedSo);
                                }}
                                onClose={() => { setSelectedId(null); setEditedQty({}); }}
                            />
                        ) : (
                            <KpiPanel list={filteredList} label={statusFilter === 'advance_booked' ? 'Advance Booked' : 'Normal'} />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function KpiPanel({ list, label }) {
    const totalQty   = list.reduce((sum, so) => sum + (Number(so.total_quantity) || 0), 0);
    const totalBoxes = list.reduce((sum, so) => sum + (Number(so.boxes_packed) || 0), 0);
    return (
        <div style={S.card}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--dpo-text-muted)', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: 14 }}>
                {label} — pending invoicing
            </div>
            <div style={S.bentoGrid}>
                <div style={S.bentoCard}>
                    <div style={S.statLabel}>Pending SOs</div>
                    <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--dpo-purple)' }}>{list.length}</div>
                </div>
                <div style={S.bentoCard}>
                    <div style={S.statLabel}>Total Qty</div>
                    <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--dpo-text)' }}>{totalQty}</div>
                </div>
                <div style={{ ...S.bentoCard, ...S.bentoSpan2 }}>
                    <div style={S.statLabel}>Total Boxes</div>
                    <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--dpo-text)' }}>{totalBoxes}</div>
                </div>
            </div>
            <p style={{ ...S.mutedNote, marginTop: 16 }}>Select an SO from the list to view line items and mark it invoiced.</p>
        </div>
    );
}

function DetailPanel({ so, isAdvanceBooked, editedQty, setEditedQty, invoiceNo, setInvoiceNo, savingQty, submitting, onSaveQuantities, onMarkInvoiced, onClose }) {
    const soQtyTotal     = (so.line_items || []).reduce((sum, li) => sum + (Number(li.so_qty) || 0), 0);
    const invoiceQtyTotal = (so.line_items || []).reduce((sum, li) => sum + (Number(li.invoice_qty) || 0), 0);

    return (
        <div style={S.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                    <div style={{ fontWeight: 700, fontSize: 17, color: 'var(--dpo-text)' }}>SO {so.voucher_no}</div>
                    {(so.buyer || so.consignee) && <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--dpo-text)' }}>{so.buyer || so.consignee}</div>}
                </div>
                <button style={S.btnGhost} onClick={onClose}>← Back to summary</button>
            </div>

            <div style={{ ...S2.statStrip, marginBottom: 20 }}>
                {[
                    { label: 'PO', value: so.order_ref_no || '—' },
                    { label: 'State Name', value: so.place_of_supply || '—' },
                    { label: 'SO Qty', value: soQtyTotal, accent: true },
                    { label: 'Boxes', value: so.boxes_packed ?? '—' },
                    { label: 'Weight (kg)', value: so.total_weight_kg ?? '—' },
                ].map((item, i) => (
                    <div key={i} style={S2.statCard}>
                        <div style={S.statLabel}>{item.label}</div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: item.accent ? 'var(--dpo-purple)' : 'var(--dpo-text)' }}>{item.value}</div>
                    </div>
                ))}
            </div>

            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--dpo-text-muted)', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: 10 }}>
                Line Items
            </div>
            <div style={{ overflowX: 'auto' }}>
                {/* color is set explicitly here (not just inherited) because the
                    dark-mode override redefines --dpo-text scoped to .acc-iq-rightcol —
                    CSS inherits the ANCESTOR's already-computed color value, so a cell
                    that never re-references var(--dpo-text) itself stays on the outer
                    (light) ink color even though the variable was redefined below it. */}
                <table className="acc-iq-table" style={{ ...S.table, color: 'var(--dpo-text)' }}>
                    <thead>
                        <tr>
                            <th style={S.th}>SKU</th>
                            <th style={S.th}>EAN</th>
                            <th style={S.th}>MRP</th>
                            {isAdvanceBooked && <th style={S.th}>Actual Qty (editable)</th>}
                            <th style={S.th}>Invoice Qty</th>
                            <th style={S.th}>SO Qty</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(so.line_items || []).map(li => (
                            <tr key={li.id} style={li.excluded ? { opacity: 0.5 } : undefined}>
                                <td style={S.td}>
                                    {li.sku_name}
                                    {li.batch_no && (
                                        <div style={S2.batchLine}>
                                            <span>Batch {li.batch_no} · Mfg {li.mfg_date || '—'} · Exp {li.exp_date || '—'}</span>
                                            <span
                                                title="Copy batch number"
                                                onClick={() => copyToClipboard(li.batch_no, () => notifyEditBlocked('Batch number copied!'))}
                                                style={S2.copyIcon}
                                            >
                                                <Copy size={ICON.xs} />
                                            </span>
                                        </div>
                                    )}
                                </td>
                                <td style={S.td}>{li.ean}</td>
                                <td style={S.td}>{li.mrp ?? '—'}</td>
                                {isAdvanceBooked && (
                                    <td style={S.td}>
                                        <input
                                            type="number"
                                            style={{ ...S.input, height: 32 }}
                                            value={editedQty[li.id] ?? li.actual_qty ?? ''}
                                            onChange={e => setEditedQty(prev => ({ ...prev, [li.id]: e.target.value }))}
                                        />
                                    </td>
                                )}
                                <td style={{ ...S.td, fontWeight: 700 }}>{li.invoice_qty}</td>
                                <td style={S.td}>{li.so_qty}</td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td colSpan={isAdvanceBooked ? 4 : 3} style={{ ...S.td, textAlign: 'right', fontWeight: 700, border: 'none' }}>Total</td>
                            <td style={{ ...S.td, fontWeight: 700, color: 'var(--dpo-purple)', border: 'none' }}>{invoiceQtyTotal}</td>
                            <td style={{ ...S.td, border: 'none' }}></td>
                        </tr>
                    </tfoot>
                </table>
            </div>

            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--dpo-border)' }}>
                {isAdvanceBooked && (
                    <button style={S.btnGhost} disabled={savingQty} onClick={onSaveQuantities}>
                        {savingQty ? 'Saving...' : 'Save Quantities'}
                    </button>
                )}
                <input
                    style={{ ...S.input, width: 220 }}
                    placeholder="Invoice number (optional)"
                    value={invoiceNo}
                    onChange={e => setInvoiceNo(e.target.value)}
                />
                <button className="acc-iq-btnPrimary" style={S.btnPrimary} disabled={submitting} onClick={onMarkInvoiced}>
                    {submitting ? 'Saving...' : 'Mark Invoice Created'}
                </button>
            </div>
        </div>
    );
}
